import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AdminTopline, Panel, ProvenanceNote } from "../../components/console";
import { LineChart } from "../../components/charts";
import { Download } from "../../components/icons";
import { api, authHeaders, describeError } from "../../lib/api";
import type { FeeBucket, PaymentRow } from "../../lib/api";
import { getCurrentRole } from "../../lib/auth";
import { ROLE_LABEL_LO } from "../../lib/roles";
import { compactLAK, fullLAK, num, periodLabel, shortDate } from "../../lib/format";
import { FinanceFilters } from "../finance/FinanceFilters";
import { useFinanceFilters, type FinanceFiltersState } from "../finance/filters";

/** หน้าละไม่เกินนี้ (เพดานของ API) — เกินจากนี้บอกบนจอว่าเห็นแค่ส่วนล่าสุด */
const PAGE_LIMIT = 200;

/** ช่วงยาวกว่านี้ (วัน) กราฟเป็นรายเดือน สั้นกว่าเป็นรายสัปดาห์ (ເດືອນນີ້/ເດືອນກ່ອນ จะได้ไม่ใช่จุดเดียว) */
const MONTHLY_FROM_DAYS = 62;

type Unit = "week" | "month";

function unitFor(f: FinanceFiltersState): Unit {
  const span = (Date.parse(f.days.to) - Date.parse(f.days.from)) / 86_400_000 + 1;
  return span > MONTHLY_FROM_DAYS ? "month" : "week";
}

const PAYMENT_METHOD_LO: Record<string, string> = {
  bcel: "BCEL",
  ldb: "ທຄຕລ (LDB)",
  jdb: "JDB",
  apb: "ທຄສ (APB)",
};

async function fetchFees(query: FinanceFiltersState["query"], unit: Unit): Promise<FeeBucket[]> {
  const { data, error, response } = await api.GET("/v1/admin/stats/fees", {
    params: { query: { ...query, unit } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items;
}

async function fetchPayments(query: FinanceFiltersState["query"]): Promise<{ items: PaymentRow[]; hasMore: boolean }> {
  const { data, error, response } = await api.GET("/v1/admin/payments", {
    params: { query: { ...query, limit: PAGE_LIMIT } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return { items: data.items, hasMore: data.has_more };
}

/** รวมทุกหมวดเป็นยอดเดียวต่อช่วง (กราฟเส้นของ mockup ไม่แยกหมวด) */
function toSeries(buckets: FeeBucket[], unit: Unit): { labels: string[]; values: number[] } {
  const byPeriod = new Map<string, number>();
  for (const b of buckets) {
    byPeriod.set(b.period_start, (byPeriod.get(b.period_start) ?? 0) + b.amount_lak);
  }
  const periods = [...byPeriod.keys()].sort();
  return {
    labels: periods.map((p) => periodLabel(p, unit)),
    values: periods.map((p) => byPeriod.get(p) ?? 0),
  };
}

/**
 * ทำไฟล์ CSV ฝั่งเบราว์เซอร์จาก JSON ที่โหลดมาแล้ว
 *
 * mockup เขียนว่า "ສົ່ງອອກ Excel" แต่ป้ายบนปุ่มเขียนว่า CSV ตามของจริง — การเขียน
 * .xlsx ต้องเพิ่ม dependency ทั้งฝั่ง server หรือ client โดยได้ผลลัพธ์เท่ากัน
 * (Excel เปิด CSV ได้) ป้ายที่บอกนามสกุลผิดจะทำให้ผู้ใช้งงตอนไฟล์ถูกดาวน์โหลด
 */
function toCSV(rows: PaymentRow[]): string {
  const header = [
    "ວັນທີຊຳລະ",
    "ເລກເອກະສານ",
    "ປະເພດເອກະສານ",
    "ຜູ້ຮ້ອງຂໍ",
    "ບ້ານ",
    "ຈຳນວນສະບັບ",
    "ຄ່າທຳນຽມ",
    "ອາກອນແສຕມ",
    "ລວມ",
    "ຊ່ອງທາງ",
  ];
  const escape = (v: string | number) => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
  };
  const lines = rows.map((r) =>
    [
      shortDate(r.paid_at),
      r.document_number || "—",
      r.document_type_name_lo,
      r.requester_name,
      r.village_name_lo || "ບໍ່ລະບຸບ້ານ",
      r.copies,
      r.amount_lak,
      r.stamp_lak,
      r.total_lak,
      PAYMENT_METHOD_LO[r.method] ?? r.method,
    ]
      .map(escape)
      .join(","),
  );
  // BOM เพื่อให้ Excel อ่านภาษาลาวเป็น UTF-8 ไม่ใช่ตัวอักษรเพี้ยน
  return "﻿" + [header.join(","), ...lines].join("\n");
}

function download(rows: PaymentRow[]) {
  const blob = new Blob([toCSV(rows)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `sdds-fees-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * ລາຍງານຄ່າທຳນຽມທີ່ແຈ້ງຊຳລະ — ตัวกรอง 2 ชั้น:
 *  - ช่วงเวลา + พื้นที่ (ใน URL, ชุดเดียวกับจอ ລາຍງານການເງິນ) ส่งให้ server — มีผลทั้งกราฟ ตัวเลข และตาราง
 *  - ชนิดเอกสาร / ช่องทาง / ค้นหา กรองในเบราว์เซอร์จากรายการที่โหลดมา — กราฟ (ยอดรวมจาก server)
 *    ไม่ตามตัวกรองชั้นนี้ จอต้องบอกตรงนั้น ไม่ให้ตัวเลขสองชุดดูขัดกันเงียบ ๆ
 */
export function DistrictRevenuePage() {
  const role = getCurrentRole();
  const f = useFinanceFilters();
  const unit = unitFor(f);
  const fees = useQuery({
    queryKey: ["stats", "fees", unit, f.key],
    queryFn: () => fetchFees(f.query, unit),
  });
  const payments = useQuery({ queryKey: ["payments", f.key], queryFn: () => fetchPayments(f.query) });

  const [docType, setDocType] = useState("");
  const [method, setMethod] = useState("");
  const [q, setQ] = useState("");

  const series = useMemo(() => toSeries(fees.data ?? [], unit), [fees.data, unit]);
  const loaded = useMemo(() => payments.data?.items ?? [], [payments.data]);
  const hasMore = payments.data?.hasMore ?? false;
  const docTypes = useMemo(
    () => [...new Set(loaded.map((r) => r.document_type_name_lo))].sort((a, b) => a.localeCompare(b, "lo")),
    [loaded],
  );
  const methods = useMemo(() => [...new Set(loaded.map((r) => r.method))].sort(), [loaded]);
  const needle = q.trim().toLowerCase();
  const rows = loaded.filter(
    (r) =>
      (!docType || r.document_type_name_lo === docType) &&
      (!method || r.method === method) &&
      (!needle ||
        [r.requester_name, r.document_number, r.village_name_lo, r.document_type_name_lo, r.reference]
          .join(" ")
          .toLowerCase()
          .includes(needle)),
  );
  const rowFiltered = Boolean(docType || method || needle);
  const total = rows.reduce((sum, r) => sum + r.total_lak, 0);
  const anyError = fees.error ?? payments.error;

  return (
    <div>
      <AdminTopline
        title="ລາຍງານຄ່າທຳນຽມທີ່ແຈ້ງຊຳລະ"
        subtitle={f.periodText}
        roleLabel={role ? ROLE_LABEL_LO[role] : undefined}
        actions={
          <button
            type="button"
            className="btn-outline btn-sm"
            disabled={rows.length === 0}
            onClick={() => {
              download(rows);
            }}
          >
            <Download size={14} /> ສົ່ງອອກ CSV
          </button>
        }
      />

      <FinanceFilters f={f} />

      {anyError ? (
        <div className="alert-error" style={{ marginBottom: "var(--space-4)" }}>
          ໂຫຼດຂໍ້ມູນບໍ່ສຳເລັດ: {anyError.message}
        </div>
      ) : null}

      {/* ADR-0015: ไม่มี payment gateway — หัวจอจึงไม่ใช้คำว่า ລາຍຮັບ ลอย ๆ */}
      <ProvenanceNote>
        ຍອດໃນລາຍງານນີ້ແມ່ນຈຳນວນທີ່ຜູ້ຂໍ <strong>ກົດຢືນຢັນວ່າຈ່າຍແລ້ວ</strong> ດ້ວຍຕົນເອງ
        ລະບົບຍັງບໍ່ໄດ້ເຊື່ອມກັບທະນາຄານ ຈຶ່ງຍັງບໍ່ແມ່ນລາຍຮັບທີ່ກວດສອບແລ້ວ
      </ProvenanceNote>

      <div className="kpi-grid" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>
        <article className="kpi">
          <div className="lbl">{rowFiltered ? "ລວມທີ່ແຈ້ງຊຳລະ (ຕາມຕົວກອງ)" : "ລວມທີ່ແຈ້ງຊຳລະ"}</div>
          <div className="val num">{payments.isPending ? "—" : compactLAK(total)}</div>
          <div className="delta" style={{ color: "var(--ink-400)" }}>
            {rows.length > 0 ? `ຈາກ ${num(rows.length)} ລາຍການ` : "ຍັງບໍ່ມີລາຍການ"}
            {hasMore ? ` · ສະເພາະ ${num(PAGE_LIMIT)} ລາຍການຫຼ້າສຸດ` : ""}
          </div>
        </article>
        <article className="kpi">
          <div className="lbl">ລາຍການທີ່ສະແດງ</div>
          <div className="val num">{payments.isPending ? "—" : num(rows.length)}</div>
          {/* ปุ่ม export ส่งออกเท่าที่แสดง (หลังกรอง) ไม่ใช่ทั้งฐานข้อมูล — ต้องบอกให้ชัด */}
          <div className="delta" style={{ color: "var(--ink-400)" }}>
            {rowFiltered ? `ຈາກທີ່ໂຫຼດມາ ${num(loaded.length)} · ` : ""}ສົ່ງອອກໄດ້ສະເພາະລາຍການທີ່ສະແດງຢູ່
          </div>
        </article>
      </div>

      <Panel
        title={unit === "month" ? "ຄ່າທຳນຽມທີ່ແຈ້ງຊຳລະລາຍເດືອນ" : "ຄ່າທຳນຽມທີ່ແຈ້ງຊຳລະລາຍອາທິດ"}
        note={rowFiltered ? "ກຣາຟຕາມຊ່ວງເວລາ ແລະ ພື້ນທີ່ເທົ່ານັ້ນ — ບໍ່ຕາມຕົວກອງປະເພດ/ຊ່ອງທາງ/ຄົ້ນຫາ" : undefined}
      >
        <LineChart
          values={series.values}
          labels={series.labels}
          title="ກຣາຟເສັ້ນຄ່າທຳນຽມທີ່ແຈ້ງຊຳລະ"
          emptyLabel={fees.isPending ? "ກຳລັງໂຫຼດ…" : "ຍັງບໍ່ມີການແຈ້ງຊຳລະໃນໄລຍະນີ້"}
          format={fullLAK}
        />
      </Panel>

      <div style={{ marginTop: "var(--space-4)" }}>
        <Panel title="ລາຍການຊຳລະ">
          <div className="filter-bar" style={{ marginBottom: "var(--space-3)" }}>
            <input
              className="filter-search"
              style={{ maxWidth: "20rem" }}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ຄົ້ນຫາ ຜູ້ຮ້ອງຂໍ / ເລກເອກະສານ / ບ້ານ"
              aria-label="ຄົ້ນຫາລາຍການຊຳລະ"
            />
            <select
              className="filter-select"
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              aria-label="ປະເພດເອກະສານ"
            >
              <option value="">ທຸກປະເພດເອກະສານ</option>
              {docTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <select
              className="filter-select"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              aria-label="ຊ່ອງທາງຊຳລະ"
            >
              <option value="">ທຸກຊ່ອງທາງ</option>
              {methods.map((m) => (
                <option key={m} value={m}>
                  {PAYMENT_METHOD_LO[m] ?? m}
                </option>
              ))}
            </select>
            {rowFiltered ? (
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => {
                  setDocType("");
                  setMethod("");
                  setQ("");
                }}
              >
                ລ້າງຕົວກອງ
              </button>
            ) : null}
          </div>
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ວັນທີ</th>
                  <th>ເອກະສານ</th>
                  <th>ຜູ້ຮ້ອງຂໍ</th>
                  <th>ບ້ານ</th>
                  <th className="is-right">ລວມ</th>
                  <th>ຊ່ອງທາງ</th>
                </tr>
              </thead>
              <tbody>
                {payments.isPending ? (
                  <tr>
                    <td colSpan={6} className="data-table__empty">
                      ກຳລັງໂຫຼດ…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="data-table__empty">
                      {rowFiltered ? "ບໍ່ມີລາຍການທີ່ກົງກັບຕົວກອງ" : "ຍັງບໍ່ມີການແຈ້ງຊຳລະໃນໄລຍະນີ້"}
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.id}>
                      <td className="num">{shortDate(r.paid_at)}</td>
                      <td>
                        {r.document_type_name_lo}
                        {r.document_number ? <div className="caption num">{r.document_number}</div> : null}
                      </td>
                      <td>{r.requester_name}</td>
                      <td>{r.village_name_lo || "ບໍ່ລະບຸບ້ານ"}</td>
                      <td className="is-right num">{fullLAK(r.total_lak)}</td>
                      <td>{PAYMENT_METHOD_LO[r.method] ?? r.method}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  );
}
